import { RouteLink } from '@mr-yum/frontend-core/dist/services/routes'
import { Alert, Button, ErrorFilledIcon, Modal } from '@mr-yum/frontend-ui'
import React, { PropsWithChildren } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { CartLayout } from '@/components/Cart/CartLayout'
import { CartLayoutHeader, CartLayoutTitle } from '@/components/Cart/shared'
import TabIcon from '@/components/Icons/Tab'
import { Meta } from '@/components/Meta/Meta'
import { Text } from '@/components/Skeleton/Text'
import {
  useOrderingTypeContext,
  useVenueContext,
} from '@/contexts/VenueOrderContext'
import { routes, VenueSlug, VenueSlugOrderingType } from '@/lib/routes'

import { GroupOrderValues } from '../GroupOrdering/GroupOrderSchema'
import { PaymentMethodKey } from './hooks/usePaymentFormMethods'
import { usePaymentControllerContext } from './PaymentController/PaymentController'
import { PaymentValues } from './PaymentSchema'
import { stripeStatementDescriptor } from './utils'

interface FakeLinkProps {
  onClick(): void
}

interface PaymentPageWrapperProps {
  pathData: VenueSlugOrderingType | VenueSlug
  title?: string
}

interface ServerValidationErrorModalProps {
  error?: string
  actionType?: 'update-cart' | 'refresh'
}

export const FakeLink = ({
  onClick,
  children,
}: PropsWithChildren<FakeLinkProps>) => {
  return (
    <span className="cursor-pointer text-accent-fallback underline">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault()
          onClick()
        }}
      >
        {children}
      </a>
    </span>
  )
}

export const PaymentMethodSkeleton = () => (
  <div className="flex items-center">
    <div className="mr-3 h-7 w-10">
      <div className="h-[26px] w-10 rounded pt-[50%] skeleton" />
    </div>
    <Text chars={12} />
  </div>
)

export const PaymentPageWrapper = ({
  title,
  children,
}: PropsWithChildren<PaymentPageWrapperProps>) => {
  const { isNewGroupOrder } = usePaymentControllerContext()

  return (
    <CartLayout title={title}>
      <Meta title="Your details - me&u" />
      {isNewGroupOrder && (
        <CartLayoutHeader>
          <CartLayoutTitle>
            <FormattedMessage
              defaultMessage="Start a new group order"
              id="eepmtb"
            />
          </CartLayoutTitle>
          <TabIcon height={28} />
        </CartLayoutHeader>
      )}
      {children}
    </CartLayout>
  )
}

export const ServerValidationErrorModal = ({
  error,
  actionType = 'update-cart',
}: ServerValidationErrorModalProps) => {
  const { orderingTypeSlug } = useOrderingTypeContext()
  const { venueSlug } = useVenueContext()
  return (
    <Modal
      title={<FormattedMessage id="z/ipXX" defaultMessage="Oops" />}
      onClose={() => null}
      isCloseable={false}
      isOpen={!!error}
    >
      <div className="p-4">
        <div className="mb-4">
          <Alert variant="critical-subtle" icon={<ErrorFilledIcon />} fullWidth>
            {error}
          </Alert>
        </div>

        {actionType === 'update-cart' && (
          <RouteLink
            route={routes.cart}
            onClick={() => location.reload()}
            pathData={{
              venueSlug,
              orderingTypeSlug,
            }}
          >
            <Button variant="primary-alternative" tabIndex={0} as="a" fullWidth>
              <FormattedMessage defaultMessage="Update your cart" id="3akIKE" />
            </Button>
          </RouteLink>
        )}

        {actionType === 'refresh' && (
          <Button
            variant="primary-alternative"
            onClick={() => location.reload()}
            tabIndex={0}
            as="a"
            fullWidth
          >
            <FormattedMessage defaultMessage="Try again" id="FazwRl" />
          </Button>
        )}
      </div>
    </Modal>
  )
}

const PaymentMethodNoticeWrapper = ({ children }: PropsWithChildren<{}>) => (
  <div className="m-auto w-3/4 px-3 text-center my-body-sm sm:px-5">
    {children}
  </div>
)

export const PaymentMethodNotice = ({
  venueName,
}: {
  venueName?: string | null
}) => {
  const { control } = useFormContext<PaymentValues | GroupOrderValues>()
  const paymentMethod = useWatch({
    name: 'paymentMethod',
    control,
  })
  if (!paymentMethod) return null

  if (paymentMethod.key.match(PaymentMethodKey.TabNew)) {
    return (
      <PaymentMethodNoticeWrapper>
        <FormattedMessage
          defaultMessage="You can share your group order or set a limit in manage my group order"
          id="qpj5+p"
        />
      </PaymentMethodNoticeWrapper>
    )
  }

  if (paymentMethod.key.match(PaymentMethodKey.ChargeToRoom)) {
    return null
  }

  if (paymentMethod.key.match(PaymentMethodKey.Cash)) {
    return (
      <PaymentMethodNoticeWrapper>
        <FormattedMessage
          defaultMessage="You can pay at the counter"
          id="Brbvtu"
        />
      </PaymentMethodNoticeWrapper>
    )
  }

  if (
    paymentMethod.key.match(PaymentMethodKey.StripeExistingCard) ||
    paymentMethod.key.match(PaymentMethodKey.StripeNewCard) ||
    paymentMethod.key.match(PaymentMethodKey.StripePaymentRequest)
  ) {
    return (
      <PaymentMethodNoticeWrapper>
        <FormattedMessage
          defaultMessage="This will appear on your statement as"
          id="weMxO+"
        />
        <pre className="m-0 inline-block">
          {venueName ? ` ${stripeStatementDescriptor(venueName)}` : ' me&u *'}
        </pre>
      </PaymentMethodNoticeWrapper>
    )
  }

  return null
}
